import type { LensDataset } from '~/types/leLens';

import type { PanelField } from './panelTypes';

/**
 * The field list's rows, from a dataset's declared columns alone.
 *
 * `dataset.columns` is the only source, per the dataset-first model: no name
 * regex, no fallback to `fields`/`dimensions`. A dataset saved before columns
 * were declared returns an empty list, which is what the panel's "Declare
 * now" state reads as its cue.
 *
 * A calculated column's formula is not on `columns` (it is typed like any
 * other column there); it is looked up on `dataset.fields` by name so a
 * field row can show it on hover, same as it would after Save on F5.
 */
export function deriveDatasetFields(dataset: LensDataset | undefined): Array<PanelField> {
  const calculated = new Map((dataset?.fields ?? []).map((field) => [field.name, field]));
  const columns = dataset?.columns ?? [];
  const declaredNames = new Set(columns.map((column) => column.name.toLowerCase()));
  const fields: Array<PanelField> = columns.map((column) => ({
    aggregate: column.aggregate,
    description: column.description,
    formula: column.kind === 'calculated' ? calculated.get(column.name)?.expression : undefined,
    kind: column.kind,
    label: column.label ?? calculated.get(column.name)?.label,
    name: column.name,
    ratio: column.ratio,
    type: column.type,
  }));
  // The query schema describes raw columns. Authored formulas can be valid
  // measures before a calculated column is promoted into that schema.
  for (const field of calculated.values()) {
    if (!declaredNames.has(field.name.toLowerCase())) {
      fields.push({
        name: field.name,
        label: field.label,
        kind: 'calculated',
        type: 'FLOAT64',
        formula: field.expression,
      });
    }
  }
  return fields;
}

/** Declared column names, for shelf validation and the missing-field check. */
export function declaredColumnNames(dataset: LensDataset | undefined): Set<string> {
  return new Set([...(dataset?.columns ?? []), ...(dataset?.fields ?? [])].map((column) => column.name.toLowerCase()));
}

/** Whether a shelf's current value is still a column the dataset declares. */
export function isDeclaredColumn(dataset: LensDataset | undefined, value: string | undefined): boolean {
  if (!value) {
    return true;
  }
  return declaredColumnNames(dataset).has(value.toLowerCase());
}
